/* eslint react-hooks/rules-of-hooks: 0 */
import * as React from 'react'
import hoistNonReactStatics from 'hoist-non-react-statics'

import { colors, fonts } from '../utils/style'

type TooltipOptions = {
  timeout: number
}

type TooltipType = {
  targetRef: React.RefObject<HTMLElement | null>
  content: React.ReactNode | (() => React.ReactNode)
  options: TooltipOptions
}

type TooltipsValueType = Array<TooltipType>

type TooltipContextValue = {
  tooltips: TooltipsValueType
  register: (
    targetRef: React.RefObject<HTMLElement | null>,
    content: React.ReactNode | (() => React.ReactNode),
    options?: Partial<TooltipOptions>,
  ) => void
  unregister: (targetRef: React.RefObject<HTMLElement | null>) => void
}

type VisibleTooltip = {
  targetNode: HTMLElement
  content: React.ReactNode | (() => React.ReactNode)
  x: number
  y: number
}

const defaultContextValue: TooltipContextValue = {
  tooltips: [],
  register: () => {},
  unregister: () => {},
}

const defaultTooltipOptions: TooltipOptions = {
  timeout: 1000,
}

export const TooltipContext: React.Context<TooltipContextValue> =
  React.createContext(defaultContextValue)

export default function TooltipHOCWrapper<T extends Record<string, any>>(
  WrappedComponent: React.ComponentType<T>,
) {
  function tooltipHOC(props: T) {
    const tooltipRef = React.useRef<HTMLDivElement>(null)
    const [tooltips, setTooltips] = React.useState<TooltipsValueType>([])
    const [visibleTooltip, setVisibleTooltip] =
      React.useState<VisibleTooltip | null>(null)

    function register(
      targetRef: React.RefObject<HTMLElement | null>,
      content: React.ReactNode | (() => React.ReactNode),
      options: Partial<TooltipOptions> = {},
    ) {
      setTooltips([
        ...tooltips,
        {
          targetRef,
          content,
          options: { ...defaultTooltipOptions, ...options },
        },
      ])
    }

    function unregister(targetRef: React.RefObject<HTMLElement | null>) {
      setTooltips(
        tooltips.filter(
          (tooltip) => tooltip.targetRef.current !== targetRef.current,
        ),
      )
    }

    React.useEffect(() => {
      function handleMouseOver(event: MouseEvent) {
        const targetNode = event.target as HTMLElement

        tooltips.forEach((tooltip) => {
          const tooltipTargetEl = tooltip.targetRef.current
          if (tooltipTargetEl && tooltipTargetEl.contains(targetNode)) {
            const { x, y, height } = tooltipTargetEl.getBoundingClientRect()

            setVisibleTooltip({
              targetNode: tooltipTargetEl,
              content: tooltip.content,
              x: x - 60,
              y: y + height,
            })
          }
        })
      }

      function handleMouseOut(event: MouseEvent) {
        let relatedTarget = event.relatedTarget as HTMLElement | null
        const tooltipEl = tooltipRef.current

        if (!visibleTooltip) return

        if (tooltipEl) {
          if (
            relatedTarget === tooltipEl ||
            tooltipEl.contains(relatedTarget) ||
            visibleTooltip.targetNode.contains(relatedTarget)
          )
            return

          setVisibleTooltip(null)
        }
      }

      document.addEventListener('mouseover', handleMouseOver, { passive: true })
      document.addEventListener('mouseout', handleMouseOut, { passive: true })

      return function cleanup() {
        document.removeEventListener('mouseover', handleMouseOver)
        document.removeEventListener('mouseout', handleMouseOut)
      }
    }, [tooltips, visibleTooltip])

    return (
      <React.Fragment>
        <TooltipContext.Provider value={{ register, unregister, tooltips }}>
          <WrappedComponent {...props} />
        </TooltipContext.Provider>
        {visibleTooltip ? (
          <div
            ref={tooltipRef}
            className="tooltip"
            style={{ left: visibleTooltip.x, top: visibleTooltip.y }}
          >
            {typeof visibleTooltip.content === 'function'
              ? React.createElement(visibleTooltip.content as React.FC)
              : visibleTooltip.content}
          </div>
        ) : null}

        <style jsx>{`
          .tooltip {
            position: absolute;
            z-index: 600;

            padding: 19px 16px;
            max-width: 330px;
            border: 1px solid #f6f6f8;
            box-sizing: border-box;
            box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.1);
            border-radius: 6px;

            font-family: ${fonts.IBMPlex};
            font-style: normal;
            font-weight: normal;
            font-size: 14px;
            line-height: 18px;

            background: ${colors.white};
            color: ${colors.inputText};
          }
        `}</style>
      </React.Fragment>
    )
  }

  hoistNonReactStatics(tooltipHOC, WrappedComponent)
  return tooltipHOC
}
